import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  Signal,
  viewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import {
  BehaviorSubject,
  debounceTime,
  filter,
  Observable,
  Subject,
  Subscription,
  throttleTime,
  withLatestFrom,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { TARGET_HEIGHT_ELEMENT } from '@fcom/common';
import { MediaQueryService } from '@fcom/common/services';
import { BookingHeaderSummary } from '@fcom/common/interfaces';
import {
  ButtonSize,
  ButtonTheme,
  ButtonType,
  IconButtonSize,
  IconButtonTheme,
  IconPosition,
  PopoverService,
} from '@fcom/ui-components';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { PseudoLocalActions } from '@fcom/core/actions';
import { ExpandComponent } from '@fcom/common/components';
import { isEmptyObjectOrHasEmptyValues } from '@fcom/core';
import { unsubscribe } from '@fcom/core/utils';

import { BOOKING_WIDGET_CONTEXT, GLOBAL_BOOKING_WIDGET_EXPANDER_KEY } from '../../constants';
import { ExpanderStatus, SelectorType, WidgetLayout } from '../../interfaces';
import { BookingWidgetGtmService } from '../../services/booking-widget-gtm.service';
import { BookingWidgetService } from '../../services/booking-widget.service';

@Component({
  selector: 'fin-global-booking-widget-summary-header',
  templateUrl: './global-booking-widget-summary-header.component.html',
  styleUrls: ['./global-booking-widget-summary-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GlobalBookingWidgetSummaryHeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  private mediaQueryService = inject(MediaQueryService);
  private store$ = inject<Store<BookingAppState>>(Store);
  private popoverService = inject(PopoverService);
  private bookingWidgetGtmService = inject(BookingWidgetGtmService);
  private bookingWidgetService = inject(BookingWidgetService);

  private readonly globalBookingWidgetSelectionChanges$ = toObservable(
    this.bookingWidgetService.globalBookingWidgetSelectionChanges
  );

  readonly EXPANDER_KEY = 'global-booking-widget-expander';
  readonly TARGET_HEIGHT_ELEMENT = TARGET_HEIGHT_ELEMENT;
  readonly SCROLL_INITIAL_DELAY_TIME = 2000;
  readonly SCROLL_CLOSE_WIDGET_DELAY_TIME = 100;
  readonly SCROLL_SETTLE_AFTER_OPEN_TIME = 500;
  readonly ButtonSize = ButtonSize;
  readonly IconPosition = IconPosition;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly ExpanderStatus = ExpanderStatus;
  readonly WidgetLayout = WidgetLayout;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  isMobile$: Observable<boolean>;
  enableNewSearchAutomatically$: Observable<boolean>;
  lastScrollTop: number = null;
  lastOpenedAt: number = null;
  scrollEvents$ = new Subject<EventTarget>();
  subscription = new Subscription();
  isExpanderOpen$ = new BehaviorSubject<boolean>(false);
  modalOpen = false;

  readonly bookingSummary = input<BookingHeaderSummary>();
  readonly enableModifySearch = input(false);

  readonly expand: Signal<ExpandComponent | undefined> = viewChild('expand');

  @HostListener('window:scroll', ['$event.target'])
  onScroll(event: EventTarget): void {
    if (this.isExpanderOpen$.getValue()) {
      this.scrollEvents$.next(event);
    }
  }

  ngOnInit(): void {
    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');
  }

  ngAfterViewInit(): void {
    const initTime = Date.now();
    this.subscription.add(
      this.scrollEvents$
        .pipe(
          withLatestFrom(this.isMobile$, this.globalBookingWidgetSelectionChanges$, this.isExpanderOpen$),
          filter(
            ([, isMobile, hasChanges, isWidgetOpen]) =>
              isWidgetOpen && !isMobile && isEmptyObjectOrHasEmptyValues(hasChanges)
          ),
          throttleTime(100),
          // filter out scroll events happening during the page load
          filter(() => initTime + this.SCROLL_INITIAL_DELAY_TIME < Date.now()),
          // filter out scroll events caused by the widget's own open animation reflow
          filter(
            () => this.lastOpenedAt === null || this.lastOpenedAt + this.SCROLL_SETTLE_AFTER_OPEN_TIME < Date.now()
          ),
          debounceTime(this.SCROLL_CLOSE_WIDGET_DELAY_TIME)
        )
        .subscribe(([event]) => this.handleScroll(event))
    );

    const expand = this.expand();
    if (expand) {
      this.subscription.add(
        expand.isOpen$.subscribe((isOpen) => {
          if (!isOpen) {
            this.lastScrollTop = null;
          } else {
            this.lastOpenedAt = Date.now();
          }

          this.isExpanderOpen$.next(isOpen);
          this.popoverService.closeByContext(BOOKING_WIDGET_CONTEXT);
          this.modalOpen = isOpen;
        })
      );
    }

    this.subscription.add(
      this.isMobile$.pipe(filter((isMobile) => !isMobile)).subscribe(() => {
        this.dispatchExpanderStatus(ExpanderStatus.OPEN);
      })
    );
  }

  dispatchExpanderStatus(expandStatus: ExpanderStatus): void {
    this.store$.dispatch(
      PseudoLocalActions.setValue({
        key: GLOBAL_BOOKING_WIDGET_EXPANDER_KEY,
        data: `${GLOBAL_BOOKING_WIDGET_EXPANDER_KEY}-${expandStatus}`,
      })
    );
  }

  modifySearchDetails(type: SelectorType, isMobile = false): void {
    if (!this.modalOpen) {
      this.bookingWidgetGtmService.trackElementEvent('global-widget-modify-search-details');
      this.dispatchExpanderStatus(ExpanderStatus.OPEN);
    }

    if (!isMobile) {
      this.bookingWidgetService.setModifyType(type);
    }
  }

  toggleDetails(): void {
    this.bookingWidgetGtmService.trackElementEvent(
      `global-widget-${this.isExpanderOpen$.getValue() ? 'cancel-search' : 'modify-search'}`
    );
    this.dispatchExpanderStatus(this.isExpanderOpen$.getValue() ? ExpanderStatus.CLOSE : ExpanderStatus.OPEN);
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  private handleScroll(event): void {
    const currentScrollTop = event.scrollingElement?.scrollTop ?? 0;
    if (this.lastScrollTop === null) {
      this.lastScrollTop = currentScrollTop;
      return;
    }
    if (currentScrollTop - this.lastScrollTop > 50 || this.lastScrollTop - currentScrollTop > 50) {
      this.lastScrollTop = null;
      this.dispatchExpanderStatus(ExpanderStatus.CLOSE);
    }
  }
}
